import React from 'react';
import ReactDOM from 'react-dom/client';
import './i18n';
import './index.css';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import App from './App';
import { AuthProvider } from './hooks';
import { registerSW } from './utils/serviceWorker';
import { logger } from './utils/logger';
import { config } from './config';

// Type for web vitals metrics
interface WebVitalsMetric {
  name: string;
  value: number;
  id: string;
  delta?: number;
}

// React Query configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (React Query v5: cacheTime -> gcTime)
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

// lite.rootify.cloud — minimální render tree bez auth/query overhead
if (window.location.hostname === 'lite.rootify.cloud') {
  const LitePreview = React.lazy(() => import('./pages/LitePreview/LitePreview'));
  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <React.Suspense fallback={<div />}>
          <LitePreview />
        </React.Suspense>
      </BrowserRouter>
    </React.StrictMode>
  );
} else {
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
        {config.isDev && <ReactQueryDevtools />}
      </QueryClientProvider>
    </React.StrictMode>
  );
}

// Register service worker for PWA functionality
if (config.enablePwa) {
  registerSW();
}

// Report web vitals
if (config.isDev) {
  import('./utils/reportWebVitals').then(({ reportWebVitals }) => {
    reportWebVitals((metric: WebVitalsMetric) => {
      // Log web vitals metrics in development
      logger.debug('Web Vitals metric', {
        component: 'WebVitals',
        metric: {
          name: metric.name,
          value: metric.value,
          id: metric.id,
          delta: metric.delta,
        },
      });
    });
  });
}
